<template>
  <div
    :key="client.id"
    class="white-background"
  >
    <v-row noGutters>
      <v-col>
        <Breadcrumbs :items="breadcrumbs" />
      </v-col>
    </v-row>
    <v-row noGutters>
      <BaseSidebar
        :items="sidebarItems"
        :title="sidebarTitle"
      />
      <v-col>
        <router-view :key="$route.fullPath" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Breadcrumbs from '@/components/shared/navigation/Breadcrumbs';
import BaseSidebar from '@/components/shared/navigation/BaseSidebar';
import clientSidebarItems from '@/constants/clientSidebarItems';
import siteSidebarItems from '@/constants/siteSidebarItems';
import roles from '@/constants/roles';
import { isEmpty } from 'lodash';
import { TYPE_MASS_BALANCE } from '@/aquassist-shared/constants/types/features';

export default {
    components: {
        Breadcrumbs,
        BaseSidebar,
    },
    data() {
        return {
            pageToDisplay: 'clientPage',
            from: 'site',
            isMassBalanceActive: false,
        };
    },
    computed: {
        ...mapGetters([
            'analysisType',
            'assessment',
            'breadcrumbs',
            'client',
            'component',
            'contact',
            'member',
            'network',
            'product',
            'riskAnalysis',
            'schedule',
            'site',
            'siteDocument',
            'teamID',
            'userLang',
            'workOrder',
            'hasRole',
            'labWorkOrder',
            'woTemplate',
        ]),
        sidebarTitle() {
            return this.$t(`${this.pageToDisplay}.pageName`);
        },
        sidebarItems() {
            if (this.pageToDisplay === 'clientPage') {
                return clientSidebarItems.filter(item => item.roles.some(role => this.hasRole(role)));
            }

            let filteredSiteSidebarItems = siteSidebarItems.filter(item => item.roles.some(role => this.hasRole(role)));

            /* If the user is a lab Technician, we doesn't want he access at WO Template */
            if (this.hasRole(roles.LABORATORY_TECHNICIAN.value) || this.hasRole(roles.LABORATORY_CHEMIST.value)) {
                filteredSiteSidebarItems = filteredSiteSidebarItems.filter(item => item.path.name !== 'siteWorkOrdersTemplate');
            }
            if (!this.isMassBalanceActive) {
                filteredSiteSidebarItems = filteredSiteSidebarItems.filter(item => item.path.name !== 'siteMassBalance');
            }

            return filteredSiteSidebarItems;
        },
    },
    watch: {
        $route() {
            this.buildFromRoute();
        },
        pageToDisplay() {
            this.buildFromRoute();
        },
    },
    created() {
        this.pageToDisplay = this.getDisplayedPage(this.$route.params);
        this.buildFromRoute();
        this.getSiteFeature();
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            // eslint-disable-next-line no-param-reassign
            vm.pageToDisplay = vm.getDisplayedPage(to.params, from?.name);
        });
    },
    // This is specific to the vuex, it checks if the client in the page is still the same that in the store, and if not, it fetch the new client
    async beforeRouteUpdate(to, from, next) {
        this.pageToDisplay = this.getDisplayedPage(to.params);
        if (from.params.clientId !== to.params.clientId) {
            if (await this.getClient(to.params.clientId)) {
                next();
            }
        } else {
            next();
        }
    },
    methods: {
        ...mapActions({
            getClient: 'getClientInfo',
            emptyBreadcrumbs: 'emptyBreadcrumbs',
            pushBreadcrumb: 'pushBreadcrumb',
        }),
        getDisplayedPage(params, fromName) {
            if (fromName === 'serviceWorkOrders') {
                this.from = fromName;
                this.buildFromRoute();
            }
            if (params.workOrderId && params.siteId && params.clientId) {
                return 'workOrderPage';
            }

            if (params.siteId && params.clientId) {
                return 'sitePage';
            }

            return 'clientPage';
        },
        async getSiteFeature() {
            const params = { team: this.teamID, type: TYPE_MASS_BALANCE };
            const { data: massBalanceFeature } = await this.$rq.getFeatures(params);
            if (!massBalanceFeature.length === 0) {
                return;
            }
            const massBalanceFeatureIds = massBalanceFeature.map(f => f.id);
            const { data: team } = await this.$rq.getTeam(this.teamID);
            const activeFeature = team?.activeFeatures?.find(
                // eslint-disable-next-line no-underscore-dangle
                f => massBalanceFeatureIds.includes(f.feature._id),
            );
            if (!isEmpty(activeFeature)) {
                this.isMassBalanceActive = true;
            }
        },
        buildFromRoute() {
            this.emptyBreadcrumbs();

            const currentPath = this.$route.matched[this.$route.matched.length - 1].path.split('/');
            const isInsideSite = currentPath.includes(':siteId');

            currentPath.shift();

            currentPath.forEach(route => {
                switch (route) {
                    case ':clientId':
                        this.pushBreadcrumb({
                            name: 'info',
                            text: `${this.client.name} [${this.client.accountNumber}]`,
                            noTranslate: true,
                        });
                        break;
                    case ':siteId':
                        this.pushBreadcrumb({
                            name: 'sitePage',
                            text: `${this.site.name} [${this.site.deliveryAccountNumber}]`,
                            noTranslate: true,
                        });
                        break;
                    case 'workOrders':
                        this.pushBreadcrumb({
                            name: this.from === 'site' ? 'siteWorkOrders' : 'serviceWorkOrders',
                            text: `${this.pageToDisplay}.${route}`,
                        });
                        break;
                    case ':contactId':
                        this.pushBreadcrumb({
                            name: 'contact',
                            text: `${this.contact.firstName} ${this.contact.lastName}`,
                            noTranslate: true,
                        });
                        break;
                    case ':componentId':
                        this.pushBreadcrumb({
                            text: this.component.name,
                            noTranslate: true,
                            disabled: true,
                        });
                        break;
                    case ':analysisTypeId':
                        this.pushBreadcrumb({
                            text: this.analysisType.name[this.userLang],
                            noTranslate: true,
                            disabled: true,
                        });
                        break;
                    case ':memberId':
                        this.pushBreadcrumb({
                            name: 'assignedUser',
                            text: `${this.member.firstName} ${this.member.lastName}`,
                            noTranslate: true,
                        });
                        break;
                    case ':networkId':
                        this.pushBreadcrumb({
                            disabled: true,
                            text: this.network.name,
                            noTranslate: true,
                        });
                        break;
                    case ':riskAnalysisId':
                        this.pushBreadcrumb({
                            name: 'riskAnalysis',
                            text: this.$t('riskAnalysis.riskAnalysisType', {
                                type: this.riskAnalysis.network.type.name,
                            }),
                            noTranslate: true,
                        });
                        break;
                    case ':assessmentId':
                        this.pushBreadcrumb({
                            name: 'legionella',
                            text: `${this.$options.filters.formatDate(this.assessment.date)} -
                            ${this.assessment.updatedBy.firstName} ${this.assessment.updatedBy.lastName}`,
                            noTranslate: true,
                        });
                        break;
                    case ':workOrderId':
                        this.pushBreadcrumb({
                            name: 'WorkOrderView',
                            text: `${this.workOrder.contractNumber || this.labWorkOrder.woNumber || this.$t('workOrderPage.pageName')}`,
                            noTranslate: true,
                        });
                        break;
                    case ':labWorkOrderId':
                        this.pushBreadcrumb({
                            name: 'labAnalysisRequests',
                            text: `${ this.labWorkOrder.woNumber || this.$t('workOrderPage.pageName')}`,
                            noTranslate: true,
                        });
                        break;
                    case 'products':
                        this.pushBreadcrumb({
                            name: isInsideSite ? 'siteProducts' : 'clientProducts',
                            text: this.$t(`${isInsideSite ? 'sitePage' : 'clientPage'}.products`),
                            noTranslate: true,
                        });
                        break;
                    case ':scheduleId':
                        this.pushBreadcrumb({
                            name: 'schedule',
                            text: `${this.$options.filters.formatDate(this.schedule.date)}
                            - ${this.schedule.updatedBy.firstName} ${this.schedule.updatedBy.lastName}`,
                            noTranslate: true,
                        });
                        break;
                    case ':productId':
                        this.pushBreadcrumb({
                            name: isInsideSite ? 'siteProduct' : 'clientProduct',
                            text: `${this.product.name} [${this.product.code}]`,
                            noTranslate: true,
                        });
                        break;
                    case 'documents':
                        this.pushBreadcrumb({
                            name: 'siteDocuments',
                            text: 'sites.document.title',
                        });
                        break;
                    case 'reports':
                        this.pushBreadcrumb({
                            name: 'siteReports',
                            text: 'sitePage.reports',
                        });
                        break;
                    case ':documentId':
                        this.pushBreadcrumb({
                            name: 'siteDocument',
                            text: this.siteDocument.code,
                            noTranslate: true,
                        });
                        break;
                    case 'flowDiagram':
                    case 'riskAnalyses':
                    case 'networks':
                    case 'schedule':
                        this.pushBreadcrumb({
                            disabled: true,
                            text: `${this.pageToDisplay}.${route}`,
                        });
                        break;
                    case 'info':
                        this.pushBreadcrumb({
                            text: `clientPage.${route}`,
                            disabled: true,
                        });
                        break;
                    case 'overview':
                    case 'form':
                    case 'measuresAndSamplesActivity':
                    case 'summary':
                        this.pushBreadcrumb({
                            text: `workOrderPage.${route}`,
                            disabled: true,
                        });
                        break;
                    case 'workOrdersTemplate':
                        this.pushBreadcrumb({
                            name: 'siteWorkOrdersTemplate',
                            text: this.$t('sitePage.workOrdersTemplate'),
                            noTranslate: true,
                        });
                        break;
                    case 'createTemplate':
                        this.pushBreadcrumb({
                            name: 'siteCreateTemplate',
                            text: this.$t('sitePage.createTemplate'),
                            noTranslate: true,
                        });
                        break;
                    case 'editTemplate':
                        this.pushBreadcrumb({
                            name: 'siteEditTemplate',
                            text: this.$t('sitePage.editTemplate'),
                            noTranslate: true,
                        });
                        break;
                    case ':woTemplateId':
                        this.pushBreadcrumb({
                            name: 'siteEditTemplate',
                            text: `${this.woTemplate.title}`,
                            noTranslate: true,
                        });
                        break;
                    default:
                        this.pushBreadcrumb({
                            name: route,
                            text: `${this.pageToDisplay}.${route}`,
                        });
                }
            });
        },
    },
};
</script>
<style lang="scss" scoped>
  @import "@/assets/styles/utils/_variables.scss";
</style>
